import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Loader2, KeyRound, ShieldCheck, PowerOff, Power, Pencil, Save, X,
} from "lucide-react";

/**
 * Admin · User & Access Management
 *
 * Simple table of the 6 department login accounts. Admin can:
 *   • Edit Email/Login
 *   • Reset Password
 *   • Activate / Deactivate
 *   • Edit Permissions matrix (VIEW / ADD / EDIT / APPROVE per module)
 *
 * DELETE is NOT exposed — permanent record deletion stays inside individual
 * detail pages and is gated to admin role at the endpoint level.
 */

const MODULE_LABELS = {
  reports: "Reports",
  inquiries: "Inquiries",
  quotations: "Quotations",
  purchase_orders: "Customer POs",
  order_acceptances: "Order Acceptances",
  receiving: "Receiving (DC)",
  engineering: "Design Queue",
  lifecycle: "Lifecycle Tracker",
  cylinders: "Cylinders",
  purchase: "Purchase & Subcontract",
  production: "Production",
  quality: "Quality",
  dispatch: "Dispatch",
  import_centre: "Import Centre",
};

const ACTIONS = ["view", "add", "edit", "approve"];

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permDialog, setPermDialog] = useState({ open: false, user: null, matrix: {} });
  const [emailDialog, setEmailDialog] = useState({ open: false, user: null, email: "" });
  const [pwDialog, setPwDialog] = useState({ open: false, user: null, pw: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/users");
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Failed to load users");
    } finally { setLoading(false); }
  }

  useEffect(() => { refresh(); }, []);

  async function toggleActive(u) {
    try {
      await api.patch(`/auth/users/${u.id}`, { is_active: !(u.is_active !== false) });
      toast.success(`${u.department || u.name} ${u.is_active === false ? "activated" : "deactivated"}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }

  async function saveEmail() {
    setSaving(true);
    try {
      await api.patch(`/auth/users/${emailDialog.user.id}`, { email: emailDialog.email });
      toast.success("Login email updated");
      setEmailDialog({ open: false, user: null, email: "" });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  }

  async function resetPassword() {
    if (pwDialog.pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pwDialog.pw !== pwDialog.confirm) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post(`/auth/users/${pwDialog.user.id}/reset-password`, { new_password: pwDialog.pw });
      toast.success(`Password reset for ${pwDialog.user.department || pwDialog.user.name}`);
      setPwDialog({ open: false, user: null, pw: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  }

  function openPermissions(u) {
    setPermDialog({ open: true, user: u, matrix: JSON.parse(JSON.stringify(u.permissions || {})) });
  }

  function togglePerm(mod, act) {
    setPermDialog((p) => ({
      ...p,
      matrix: {
        ...p.matrix,
        [mod]: { ...(p.matrix[mod] || {}), [act]: !p.matrix?.[mod]?.[act] },
      },
    }));
  }

  async function savePermissions() {
    setSaving(true);
    try {
      await api.put(`/auth/users/${permDialog.user.id}/permissions`, { permissions: permDialog.matrix });
      toast.success(`Permissions updated for ${permDialog.user.department || permDialog.user.name}`);
      setPermDialog({ open: false, user: null, matrix: {} });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  }

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-900" /></div>;

  return (
    <div data-testid="user-mgmt-page" className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Administration</div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>User &amp; Access Management</h1>
        <p className="text-sm text-slate-500 mt-1">
          HISPL NEXUS has six department login accounts. Admin edits email, password, activation
          and per-module permissions here. Individual employees are NOT ERP login accounts.
        </p>
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Department</TableHead>
              <TableHead>Login Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => {
              const permCount = Object.values(u.permissions || {}).reduce(
                (s, row) => s + ACTIONS.filter((a) => row?.[a]).length, 0);
              const isAdmin = u.role === "admin" || u.role === "super_admin";
              return (
                <TableRow key={u.id} data-testid={`user-row-${u.role}`}>
                  <TableCell>
                    <div className="font-semibold text-slate-900">{u.department || u.name}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{u.role}</div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs text-slate-800">{u.email}</span>
                    {!isAdmin && (
                      <Button variant="ghost" size="icon" className="ml-1 h-6 w-6"
                              data-testid={`edit-email-${u.role}`}
                              onClick={() => setEmailDialog({ open: true, user: u, email: u.email })}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.is_active === false ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
                        <PowerOff className="w-3 h-3" /> Inactive
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                        <Power className="w-3 h-3" /> Active
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-slate-600">{permCount} of {Object.keys(MODULE_LABELS).length * ACTIONS.length} enabled</span>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="outline" size="sm" data-testid={`edit-perms-${u.role}`}
                            onClick={() => openPermissions(u)} disabled={isAdmin}
                            title={isAdmin ? "Admin permissions are fixed" : "Edit permission matrix"}>
                      <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Permissions
                    </Button>
                    <Button variant="outline" size="sm" data-testid={`reset-pw-${u.role}`}
                            onClick={() => setPwDialog({ open: true, user: u, pw: "", confirm: "" })}>
                      <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset Password
                    </Button>
                    {!isAdmin && (
                      <Button variant="outline" size="sm" data-testid={`toggle-active-${u.role}`}
                              onClick={() => toggleActive(u)}>
                        {u.is_active === false ? <><Power className="w-3.5 h-3.5 mr-1" /> Activate</> : <><PowerOff className="w-3.5 h-3.5 mr-1" /> Deactivate</>}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* --------- Permission Matrix Dialog --------- */}
      <Dialog open={permDialog.open} onOpenChange={(v) => !v && setPermDialog({ open: false, user: null, matrix: {} })}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Permissions · {permDialog.user?.department}</DialogTitle>
            <DialogDescription>Tick the boxes to grant capability. Removing "View" hides the module from the sidebar. Delete stays admin-only.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm" data-testid="perm-matrix">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Module</th>
                  {ACTIONS.map((a) => (
                    <th key={a} className="py-2 px-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-center">{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(MODULE_LABELS).map(([mod, label]) => (
                  <tr key={mod} className="border-b border-slate-100">
                    <td className="py-2 px-2 font-medium text-slate-800">{label}</td>
                    {ACTIONS.map((a) => (
                      <td key={a} className="py-2 px-2 text-center">
                        <input type="checkbox"
                               data-testid={`perm-${mod}-${a}`}
                               checked={!!permDialog.matrix?.[mod]?.[a]}
                               onChange={() => togglePerm(mod, a)}
                               className="w-4 h-4 cursor-pointer accent-red-700" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermDialog({ open: false, user: null, matrix: {} })}>
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
            <Button onClick={savePermissions} disabled={saving} data-testid="save-perms"
                    className="bg-red-700 hover:bg-red-800 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save Permissions</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --------- Email Dialog --------- */}
      <Dialog open={emailDialog.open} onOpenChange={(v) => !v && setEmailDialog({ open: false, user: null, email: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Login Email · {emailDialog.user?.department}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Label className="text-xs uppercase tracking-wider text-slate-600">Login Email</Label>
            <Input type="email" value={emailDialog.email}
                   onChange={(e) => setEmailDialog({ ...emailDialog, email: e.target.value })}
                   className="font-mono" data-testid="edit-email-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog({ open: false, user: null, email: "" })}>Cancel</Button>
            <Button onClick={saveEmail} disabled={saving} className="bg-red-700 hover:bg-red-800 text-white" data-testid="save-email">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --------- Reset Password Dialog --------- */}
      <Dialog open={pwDialog.open} onOpenChange={(v) => !v && setPwDialog({ open: false, user: null, pw: "", confirm: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password · {pwDialog.user?.department}</DialogTitle>
            <DialogDescription>The current department password will be replaced. Notify the department after resetting.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">New Password</Label>
              <Input type="password" value={pwDialog.pw}
                     onChange={(e) => setPwDialog({ ...pwDialog, pw: e.target.value })}
                     data-testid="new-pw-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Confirm Password</Label>
              <Input type="password" value={pwDialog.confirm}
                     onChange={(e) => setPwDialog({ ...pwDialog, confirm: e.target.value })}
                     data-testid="confirm-pw-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwDialog({ open: false, user: null, pw: "", confirm: "" })}>Cancel</Button>
            <Button onClick={resetPassword} disabled={saving} className="bg-red-700 hover:bg-red-800 text-white" data-testid="save-pw">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
